/**
 * 后台版式 topnav（主题 A 的外壳）：顶部一级模块导航 + 左侧二级浅色细栏，**不用多标签页**。
 * 样稿：designs/mockups/theme/个人站主题A-完整样稿.html ② ③（tag theme-a-mockup-v1）。
 *
 * · 一级 / 二级菜单都从同一份 NAV_ITEMS 生成（navConfig.groupVisibleModules），权限过滤照旧（visibleNavItems）；页面内容组件与侧栏版式共用，不复制。
 * · 待办徽标走同一个 useNavBadges；右上角铃铛 / 账号菜单复用 NotificationBell / UserMenu。
 * · Ctrl K（Mac ⌘K）打开命令面板。
 */
import { Suspense, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useEdition } from "@/theme/useTheme";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { ChevronDown, Mountain, Search } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/store/authStore";
import { settingsApi } from "@/services/settings";
import { apiConfig } from "@/services/api";
import { RoutePermissionGate } from "@/auth/RoutePermissionGate";
import { NotificationBell } from "./NotificationBell";
import { UserMenu } from "./UserMenu";
import { CommandPalette } from "./CommandPalette";
import { SubscriptionTools } from "./SubscriptionTools";
import { TodaySubnav } from "./TodaySubnav";
// ═ COMPANY-STRIP BEGIN ═
import { MarketingSubnav } from "@/pages/marketing/MarketingSubnav";
// ═ COMPANY-STRIP END ═
import { useNavBadges } from "./useNavBadges";
import { activeModuleId, groupVisibleModules, isNavItemActive, visibleNavItems, type NavItem, type VisibleModule } from "./navConfig";
import { useNavFlags } from "./useNavFlags";

const brandImg = (u: string) => (u.startsWith("http") ? u : `${apiConfig.base}${u}`);

export function TopNavLayout() {
  const permissions = useAuthStore((s) => s.user?.permissions);
  const edition = useEdition();
  const navFlags = useNavFlags();
  const navFlagsKey = JSON.stringify(navFlags);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const items = useMemo(() => visibleNavItems(permissions, edition, navFlags), [permissions, edition, navFlagsKey]);
  const modules = useMemo(() => groupVisibleModules(items), [items]);
  const badgeFor = useNavBadges(items);
  const loc = useLocation();
  const { data: branding } = useQuery({ queryKey: ["branding"], queryFn: settingsApi.getBranding, staleTime: 5 * 60_000 });
  const [cmdOpen, setCmdOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);

  // Ctrl K / ⌘K 打开命令面板
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCmdOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // SAAS-2 · 「更多」下拉用 fixed 定位贴着按钮：header 是 overflow-x-auto（窄屏横向滚），absolute 的下拉会被它裁掉（只露一条线），
  // 应用中心入口就在「更多」里，所以一并修；收起状态一个像素不变。滚动 / 改窗口大小时收起。
  const [morePos, setMorePos] = useState<{ top: number; left: number } | null>(null);
  useEffect(() => {
    if (!moreOpen || !moreRef.current) return;
    const r = moreRef.current.getBoundingClientRect();
    setMorePos({ top: r.bottom + 4, left: Math.min(r.left, window.innerWidth - 200) });
    const close = () => setMoreOpen(false);
    window.addEventListener("resize", close);
    window.addEventListener("scroll", close, true);
    return () => {
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [moreOpen]);

  // 点外面收起「更多」
  useEffect(() => {
    if (!moreOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) setMoreOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [moreOpen]);

  const activeId = activeModuleId(modules, loc.pathname, loc.search);
  const main = modules.filter((m) => m.id !== "more");
  const more = modules.find((m) => m.id === "more");
  const activeModule = modules.find((m) => m.id === activeId && m.id !== "more");
  // 二级细栏：当前模块的菜单项；只有一项的模块（今日）不显示细栏；「更多」里的页面用下拉进入，不占细栏
  const subItems: NavItem[] = activeModule && activeModule.items.length > 1 ? activeModule.items : [];
  // SAAS-2 · D：订阅版「今日」模块有自己的细栏；其它模块照旧（只有一项的模块不出细栏）
  let customSubnav: ReactNode = edition === "subscription" && activeId === "today" ? <TodaySubnav /> : null;
  // ═ COMPANY-STRIP BEGIN ═
  if (edition === "subscription" && activeId === "marketing") customSubnav = <MarketingSubnav />;   // MKTSYNC-1 · C：内容营销细栏（样稿 ④ 左栏）
  // ═ COMPANY-STRIP END ═
  const badgeSum = (m: VisibleModule) => m.items.reduce((n, it) => n + (badgeFor(it) ?? 0), 0);

  return (
    <div className="flex min-h-screen flex-col bg-paper" data-testid="topnav-layout">
      <header className="sticky top-0 z-30 flex h-[52px] items-center gap-0.5 overflow-x-auto border-b border-slate-200 bg-white px-4" data-testid="topnav">
        <div className="mr-4 flex shrink-0 items-center gap-2 whitespace-nowrap font-extrabold text-brand">
          {branding?.logo_url ? (
            <img src={brandImg(branding.logo_url)} alt="logo" className="h-6 w-6 rounded-md object-cover" />
          ) : (
            <span className="flex h-6 w-6 items-center justify-center rounded-md bg-brand"><Mountain className="h-3.5 w-3.5 text-white" strokeWidth={2.2} /></span>
          )}
          <span className="text-[15px]">{branding?.brand_name || "悠途"}</span>
          <small className="text-[11px] font-normal text-slate-400">运营后台</small>
        </div>

        {main.map((m) => {
          const badge = badgeSum(m);
          return (
            <NavLink
              key={m.id}
              to={m.items[0].to}
              data-testid={`topnav-${m.id}`}
              className={cn(
                "relative shrink-0 whitespace-nowrap rounded-md px-3 py-1.5 text-[13px] text-slate-600 hover:bg-slate-50",
                activeId === m.id && "bg-brand-soft font-semibold text-brand",
              )}
            >
              {m.labelCn}
              {badge > 0 && <span className="ml-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white">{badge}</span>}
            </NavLink>
          );
        })}

        {more && (
          <div className="relative shrink-0" ref={moreRef}>
            <button
              type="button"
              data-testid="topnav-more"
              aria-expanded={moreOpen}
              onClick={() => setMoreOpen((v) => !v)}
              className={cn("flex items-center gap-1 whitespace-nowrap rounded-md px-3 py-1.5 text-[13px] text-slate-600 hover:bg-slate-50", activeId === "more" && "bg-brand-soft font-semibold text-brand")}
            >
              更多 <ChevronDown className="h-3.5 w-3.5" />
            </button>
            {moreOpen && (
              <div className="z-40 w-48 rounded-lg border border-slate-200 bg-white p-1 shadow-lg" role="menu" style={morePos ? { position: "fixed", top: morePos.top, left: morePos.left } : { position: "fixed", visibility: "hidden" }}>
                {more.items.map((it) => (
                  <NavLink
                    key={it.id}
                    to={it.to}
                    onClick={() => setMoreOpen(false)}
                    className={cn("flex items-center justify-between rounded-md px-3 py-2 text-[13px] text-slate-700 hover:bg-slate-50", isNavItemActive(it, loc.pathname, loc.search) && "bg-brand-soft font-semibold text-brand")}
                  >
                    {it.labelCn}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        )}

        <button
          type="button"
          onClick={() => setCmdOpen(true)}
          aria-label="打开命令面板"
          data-testid="cmdk-trigger"
          className="ml-auto flex min-w-[200px] shrink-0 items-center justify-between gap-2 whitespace-nowrap rounded-md border border-slate-200 px-2.5 py-1.5 text-[13px] text-slate-400 hover:bg-slate-50"
        >
          <span className="flex items-center gap-1.5"><Search className="h-3.5 w-3.5" /> 搜订单号 / 团号 / 功能</span>
          <kbd className="rounded border border-slate-200 px-1 text-[11px]">Ctrl K</kbd>
        </button>
        <div className="ml-2 flex shrink-0 items-center gap-2">
          <SubscriptionTools />
          <NotificationBell />
          <UserMenu />
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        {/* SAAS-2 · D：订阅版的「今日」模块有自己的细栏（经营助手 / 今日出团 / 待确认 N / 付款待初筛 N / 交接墙）；企业版照旧（「今日」只有一项不出细栏） */}
        {customSubnav ?? (subItems.length > 0 && (
          <nav className="hidden w-44 shrink-0 flex-col gap-0.5 border-r border-slate-200 bg-white p-2 md:flex" data-testid="subnav" aria-label="二级导航">
            {subItems.map((it) => {
              const badge = badgeFor(it);
              return (
                <NavLink
                  key={it.id}
                  to={it.to}
                  className={cn(
                    "flex items-center justify-between rounded-md px-2.5 py-1.5 text-[13px] text-slate-600 hover:bg-slate-50",
                    it.sub && "pl-5 text-[12px]",
                    isNavItemActive(it, loc.pathname, loc.search) && "bg-brand-soft font-semibold text-brand",
                  )}
                >
                  <span>{it.labelCn}</span>
                  {badge ? <em className="min-w-[18px] rounded-full bg-rose-500 px-1.5 text-center text-[10px] font-bold not-italic text-white">{badge}</em> : null}
                </NavLink>
              );
            })}
          </nav>
        ))}
        <main className="min-w-0 flex-1">
          <Suspense fallback={<div className="flex h-64 items-center justify-center text-sm text-slate-400">加载中…</div>}>
            <RoutePermissionGate>
              <Outlet />
            </RoutePermissionGate>
          </Suspense>
        </main>
      </div>

      <CommandPalette open={cmdOpen} onClose={() => setCmdOpen(false)} />
    </div>
  );
}
